import React from "react";
import { pushSupported } from "@/lib/push";
import { Loader2, AlertTriangle, ExternalLink, BellRing, Send, SlidersHorizontal } from "lucide-react";

export default function AlertsTab({ alerts, settings, setSettings, updateSettings, pushOn, pushBusy, enableBrowserAlerts, sendTestAlert, snooze, resumeAlerts }) {
  return (
    <div data-testid="ptab-content-alerts">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <p className="text-sm text-[var(--text-2)]">High-exposure players who just hit the news — manage your concentration risk.</p>
        {pushSupported() && (
          <div className="flex items-center gap-2">
            {pushOn ? (
              <span className="inline-flex items-center gap-1 text-xs text-[var(--accent)]" data-testid="pf-push-on"><BellRing className="w-3.5 h-3.5" /> Browser alerts on</span>
            ) : (
              <button onClick={enableBrowserAlerts} disabled={pushBusy} className="btn-secondary text-xs inline-flex items-center gap-1.5" data-testid="pf-enable-push">
                <BellRing className="w-3.5 h-3.5" /> Enable browser alerts
              </button>
            )}
            <button onClick={sendTestAlert} disabled={pushBusy} className="btn-secondary text-xs inline-flex items-center gap-1.5" data-testid="pf-test-push">
              <Send className="w-3.5 h-3.5" /> Send test
            </button>
          </div>
        )}
      </div>
      {settings && (
        <div className="card-bordered p-4 mb-4 flex flex-wrap items-center gap-x-8 gap-y-3" data-testid="alert-prefs">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.2em] text-[var(--text-3)] font-mono">
            <SlidersHorizontal className="w-3.5 h-3.5" /> ALERT PREFERENCES
          </div>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-[var(--text-2)]">Exposure threshold</span>
            <input
              type="number" min="1" max="100"
              value={settings.alert_exposure_threshold}
              onChange={(e) => setSettings({ ...settings, alert_exposure_threshold: Number(e.target.value) })}
              onBlur={(e) => updateSettings({ alert_exposure_threshold: Number(e.target.value) })}
              className="input-dark w-20 py-1 text-center" data-testid="threshold-input"
            />
            <span className="text-[var(--text-3)]">%</span>
          </label>
          <button
            onClick={() => updateSettings({ mute_waivers: !settings.mute_waivers })}
            className="flex items-center gap-2 text-sm" data-testid="mute-waivers-toggle">
            <span className={`w-9 h-5 rounded-full relative transition-colors ${settings.mute_waivers ? "bg-[var(--accent)]" : "bg-[var(--border)]"}`}>
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-black transition-all ${settings.mute_waivers ? "left-[18px]" : "left-0.5"}`} />
            </span>
            <span className="text-[var(--text-2)]">Mute waiver pushes</span>
          </button>
          <div className="flex items-center gap-2 text-sm" data-testid="snooze-controls">
            {settings.alerts_snoozed_until && new Date(settings.alerts_snoozed_until) > new Date() ? (
              <>
                <span className="text-[var(--amber)] text-xs" data-testid="snooze-status">Paused until {new Date(settings.alerts_snoozed_until).toLocaleDateString()}</span>
                <button onClick={resumeAlerts} className="btn-secondary text-xs py-1" data-testid="resume-alerts-btn">Resume</button>
              </>
            ) : (
              <>
                <span className="text-[var(--text-3)] text-xs">Pause alerts:</span>
                <button onClick={() => snooze(1)} className="btn-secondary text-xs py-1" data-testid="snooze-1d">1 day</button>
                <button onClick={() => snooze(7)} className="btn-secondary text-xs py-1" data-testid="snooze-7d">1 week</button>
              </>
            )}
          </div>
        </div>
      )}
      {alerts === null ? (
        <div className="card-bordered p-10 flex justify-center"><Loader2 className="w-5 h-5 spin text-[var(--accent)]" /></div>
      ) : alerts.length === 0 ? (
        <div className="card-bordered p-8 text-center text-sm text-[var(--text-3)]" data-testid="alerts-empty">No exposure alerts right now. We'll flag high-exposure players the moment they make news.</div>
      ) : (
        <div className="space-y-2">
          {alerts.map((a, i) => (
            <div key={`${a.player_id || a.name}-${i}`} className="card-bordered p-4 border-l-2 border-l-[var(--negative)]" data-testid={`alert-${i}`}>
              <div className="flex items-center justify-between">
                <div className="font-display font-bold">{a.name} <span className="text-xs text-[var(--text-3)]">{a.position}</span></div>
                <div className="text-xs font-mono text-[var(--negative)]">{a.exposure_pct}% EXPOSURE · {a.leagues_owned}/{a.total_leagues} leagues</div>
              </div>
              <div className="text-sm text-[var(--text-2)] mt-1 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-[var(--negative)] mt-0.5 shrink-0" />
                <span>{a.headline}</span>
              </div>
              {a.url && <a href={a.url} target="_blank" rel="noreferrer" className="text-xs text-[var(--accent)] hover:underline inline-flex items-center gap-1 mt-2">Read more <ExternalLink className="w-3 h-3" /></a>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
